import { useEffect, useState } from "react";
import { ScenarioCard } from "./ScenarioCard";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Scenario {
  id: string;
  session_id: string;
  name: string;
  description: string;
  scenario_type: string;
  order_index: number;
}

interface ScenarioBuilderProps {
  sessionId: string;
  onContinue?: () => void;
}

const DEFAULT_SCENARIOS = [
  {
    scenario_type: "option",
    name: "Option A",
    description: "Beschreibe deine erste Option hier...",
    order_index: 0,
  },
  {
    scenario_type: "option",
    name: "Option B",
    description: "Beschreibe deine zweite Option hier...",
    order_index: 1,
  },
  {
    scenario_type: "baseline",
    name: "Baseline",
    description: "Was passiert, wenn du weitermachst wie bisher?",
    order_index: 2,
  },
];

export const ScenarioBuilder = ({ sessionId, onContinue }: ScenarioBuilderProps) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadScenarios();
  }, [sessionId]);

  const loadScenarios = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("scenarios")
        .select("*")
        .eq("session_id", sessionId)
        .order("order_index");

      if (error) throw error;

      if (!data || data.length === 0) {
        // Create default scenarios
        await createDefaultScenarios();
      } else {
        setScenarios(data as Scenario[]);
      }
    } catch (error) {
      console.error("Error loading scenarios:", error);
      toast({
        title: "Fehler beim Laden",
        description: "Die Szenarien konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultScenarios = async () => {
    try {
      const scenariosToCreate = DEFAULT_SCENARIOS.map((s) => ({
        session_id: sessionId,
        ...s,
      }));

      const { data, error } = await supabase
        .from("scenarios")
        .insert(scenariosToCreate)
        .select();

      if (error) throw error;
      setScenarios(data as Scenario[]);
    } catch (error) {
      console.error("Error creating default scenarios:", error);
      toast({
        title: "Fehler",
        description: "Die Standard-Szenarien konnten nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateAISuggestions = async () => {
    setIsGeneratingAI(true);
    try {
      const { data, error } = await supabase.functions.invoke('phase2-scenarios', {
        body: { session_id: sessionId }
      });

      if (error) throw error;

      if (!data?.success || !data?.scenarios) {
        throw new Error("Ungültige Antwort vom AI-Service");
      }

      // Update scenarios with AI suggestions
      const optionScenarios = scenarios.filter(s => s.scenario_type === "option");
      const updates = [
        {
          id: optionScenarios[0]?.id,
          type: "option",
          suggestion: data.scenarios.option_a
        },
        {
          id: optionScenarios[1]?.id,
          type: "option",
          suggestion: data.scenarios.option_b
        },
        {
          id: scenarios.find(s => s.scenario_type === "baseline")?.id,
          type: "baseline",
          suggestion: data.scenarios.baseline
        }
      ];

      for (const update of updates) {
        if (update.id && update.suggestion) {
          const { error: updateError } = await supabase
            .from("scenarios")
            .update({
              name: update.suggestion.name,
              description: update.suggestion.description,
            })
            .eq("id", update.id);

          if (updateError) {
            console.error("Error updating scenario:", updateError);
          }
        }
      }

      // Reload scenarios to show updates
      await loadScenarios();

      toast({
        title: "AI-Vorschläge generiert",
        description: "Die Szenarien wurden mit intelligenten Vorschlägen befüllt.",
        duration: 5000,
      });
    } catch (error: any) {
      console.error("Error generating AI suggestions:", error);
      
      let errorMessage = "Die AI-Vorschläge konnten nicht generiert werden.";
      if (error.message?.includes("Rate limit")) {
        errorMessage = "Rate Limit erreicht. Bitte versuche es später erneut.";
      } else if (error.message?.includes("Payment required")) {
        errorMessage = "Bitte füge Credits zu deinem Workspace hinzu.";
      }
      
      toast({
        title: "Fehler",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleSaveScenario = async (
    scenarioId: string,
    data: { title: string; description: string }
  ) => {
    try {
      const { error } = await supabase
        .from("scenarios")
        .update({
          name: data.title,
          description: data.description,
        })
        .eq("id", scenarioId);

      if (error) throw error;

      setScenarios((prev) =>
        prev.map((s) =>
          s.id === scenarioId
            ? { ...s, name: data.title, description: data.description }
            : s
        )
      );

      toast({
        title: "Gespeichert",
        description: "Die Änderungen wurden gespeichert.",
      });
    } catch (error) {
      console.error("Error saving scenario:", error);
      toast({
        title: "Fehler",
        description: "Die Änderungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-2 flex-1">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              Welche Optionen möchtest du vergleichen?
            </h2>
            <p className="text-muted-foreground">
              Definiere die drei Szenarien, die du systematisch bewerten möchtest.
            </p>
          </div>
          <Button
            onClick={handleGenerateAISuggestions}
            disabled={isGeneratingAI}
            variant="outline"
            className="gap-2 w-full md:w-auto"
          >
            {isGeneratingAI ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generiere...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                AI-Vorschläge
              </>
            )}
          </Button>
        </div>
      </div>

      {onContinue && scenarios.length > 0 && (
        <div className="flex justify-end mb-6">
          <Button onClick={onContinue} size="lg">
            Mit Szenarien fortfahren
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {scenarios.map((scenario, index) => (
          <ScenarioCard
            key={scenario.id}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` } as React.CSSProperties}
            id={scenario.id}
            title={scenario.name}
            description={scenario.description || ""}
            scenarioType={scenario.scenario_type}
            orderIndex={scenario.order_index}
            onSave={(data) => handleSaveScenario(scenario.id, data)}
          />
        ))}
      </div>
    </div>
  );
};
